import {
  Bill,
  BillSubscriptionEndPolicyEnum,
  BillSubscriptionIntervalTypeEnum,
  DeliveryPreferenceType,
} from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { date, mixed, object, SchemaOf, string, StringSchema } from 'yup';

import { PaymentFlowFormFields } from '../../types';

export const usePaymentFlowFormSchema = (bill?: Bill): SchemaOf<PaymentFlowFormFields> => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    amountToPay: string()
      .required(formatMessage('activities.paymentFlow.form.content.amountToPay.required'))
      .test(
        'validGreaterThanZero',
        formatMessage('activities.paymentFlow.form.content.amountToPay.validGreaterThanZero.label'),
        (value?: string) => {
          const number = Number(value);
          return number > 0;
        }
      )
      .test(
        'validLessThanOrEqualBalance',
        formatMessage('activities.paymentFlow.form.content.amountToPay.validLessThanOrEqualBalance.label'),
        (value?: string) => {
          if (!bill) {
            return true;
          }

          const number = Number(value);
          return number <= bill.balance;
        }
      )
      .test(
        'validGreaterThanOrEqualMinimum',
        formatMessage('activities.paymentFlow.form.content.amountToPay.validGreaterThanOrEqualMinimum.label'),
        (value?: string) => {
          if (!bill?.minimumAmount) {
            return true;
          }

          const number = Number(value);
          return number >= bill.minimumAmount;
        }
      ),
    vendorId: string(),
    deliveryMethodId: string().required(),
    fundingSourceId: string().required(),
    scheduleDate: date().required(),
    deliveryDate: date().required(),
    deliveryPreference: new StringSchema<DeliveryPreferenceType>(),
    noteToVendor: string().optional(),
    vendorEmail: string()
      .optional()
      .email(formatMessage('activities.paymentFlow.form.content.vendorEmail.validation.format')),

    lastAmount: string(),

    paymentPurpose: string(),
    description: string(),
    invoice: mixed<File>(),

    recurrenceType: new StringSchema<'one_time' | 'recurring'>().oneOf(['one_time', 'recurring']).required(),
    startDate: mixed().when('recurrenceType', {
      is: 'recurring',
      then: date().required(),
      otherwise: date().notRequired(),
    }),
    intervalType: mixed().when('recurrenceType', {
      is: 'recurring',
      then: new StringSchema<BillSubscriptionIntervalTypeEnum>().required(),
      otherwise: new StringSchema<BillSubscriptionIntervalTypeEnum>().notRequired(),
    }),
    endPolicy: mixed().when('recurrenceType', {
      is: 'recurring',
      then: new StringSchema<BillSubscriptionEndPolicyEnum>().required(),
      otherwise: new StringSchema<BillSubscriptionEndPolicyEnum>().notRequired(),
    }),
    endDate: mixed().when('endPolicy', {
      is: BillSubscriptionEndPolicyEnum.EndDate,
      then: date().required(),
      otherwise: date().notRequired(),
    }),
  });
};
